import { type KeyboardEvent, type ReactNode, useRef, useState } from 'react';
import { Alert, Button, Card, TrashIcon } from './ui';

export interface ListEntry {
  id: string;
  name: string;
  detail?: string;
  itemCount: number;
}

/**
 * Titled list with add, rename, and delete. Clicking a name, or Enter or Space on it, starts
 * the rename. Delete is disabled while items still point at an entry: archiving it would
 * leave them showing a blank name. `readOnly` shows the entries alone.
 */
export function EditableList({
  title,
  noun,
  entries,
  emptyText,
  renderAdd,
  onRename,
  onDelete,
  onError,
  readOnly = false,
}: {
  title: string;
  noun: string;
  entries: ListEntry[];
  emptyText: string;
  /** The caller's add form, which calls `done` when it closes. */
  renderAdd: (done: () => void) => ReactNode;
  onRename: (id: string, name: string) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  onError: (e: unknown) => void;
  readOnly?: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const rename = (entry: ListEntry, name: string) => {
    setEditing(null);
    if (name.trim() && name.trim() !== entry.name) {
      void onRename(entry.id, name.trim()).catch(onError);
    }
  };

  return (
    <Card label={title}>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-semibold text-content-primary">{title}</h2>
        {!readOnly && (
          <IconButton label={`Add ${noun}`} onClick={() => setAdding(true)}>
            <span className="text-xl leading-none">+</span>
          </IconButton>
        )}
      </div>
      {adding && renderAdd(() => setAdding(false))}
      <ul className="divide-y divide-divider">
        {entries.map((entry) => (
          <li key={entry.id} className="flex min-h-11 items-center gap-2 py-1">
            {editing === entry.id ? (
              <InlineInput
                label={`Rename ${entry.name}`}
                initial={entry.name}
                onDone={(name) => rename(entry, name)}
              />
            ) : (
              <button
                type="button"
                title={readOnly ? undefined : 'Rename'}
                onClick={readOnly ? undefined : () => setEditing(entry.id)}
                className="min-w-0 flex-1 touch-manipulation truncate text-left text-sm text-content-primary"
              >
                <span className="font-medium">{entry.name}</span>
                {entry.detail && <span className="text-content-tertiary"> · {entry.detail}</span>}
                <span className="text-content-tertiary"> · {entry.itemCount} items</span>
              </button>
            )}
            {!readOnly && (
              <IconButton
                label={`Delete ${entry.name}`}
                disabled={entry.itemCount > 0}
                title={entry.itemCount > 0 ? `In use by ${entry.itemCount} items` : undefined}
                onClick={() => void onDelete(entry.id).catch(onError)}
              >
                <TrashIcon />
              </IconButton>
            )}
          </li>
        ))}
      </ul>
      {entries.length === 0 && !adding && (
        <p className="py-2 text-sm text-content-tertiary">{emptyText}</p>
      )}
    </Card>
  );
}

export interface AddFieldProps {
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onInput: () => void;
}

/** Every list's add form: the caller's fields, an alert, and Cancel / Create buttons. */
export function AddForm({
  noun,
  ready,
  issue,
  onCreate,
  done,
  children,
}: {
  noun: string;
  ready: boolean;
  issue?: string;
  onCreate: () => Promise<unknown>;
  done: () => void;
  children: (field: AddFieldProps) => ReactNode;
}) {
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  // State alone lets a second Enter in the same tick through, before the re-render lands.
  const inFlight = useRef(false);

  const create = async () => {
    if (!ready || inFlight.current) return;
    inFlight.current = true;
    setWorking(true);
    try {
      await onCreate();
      done();
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not create the ${noun}`);
    } finally {
      inFlight.current = false;
      setWorking(false);
    }
  };
  const field: AddFieldProps = {
    onKeyDown: (e) => {
      if (e.key === 'Enter') void create();
      // Handled here, so the drawer around the form stays open.
      if (e.key === 'Escape') {
        e.preventDefault();
        done();
      }
    },
    onInput: () => setError(''),
  };

  return (
    <div className="space-y-2 py-1">
      <div className="flex gap-2">{children(field)}</div>
      {(error || issue) && <Alert>{error || issue}</Alert>}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={done}>
          Cancel
        </Button>
        <Button onClick={() => void create()} disabled={!ready || working}>
          Create {noun}
        </Button>
      </div>
    </div>
  );
}

function InlineInput({
  label,
  initial,
  placeholder,
  onDone,
}: {
  label: string;
  initial: string;
  placeholder?: string;
  onDone: (value: string) => void;
}) {
  const [value, setValue] = useState(initial);
  const [done, setDone] = useState(false);
  const finish = (v: string) => {
    if (done) return;
    setDone(true);
    onDone(v);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') finish(value);
    if (e.key === 'Escape') {
      e.preventDefault();
      finish('');
    }
  };
  return (
    <input
      // biome-ignore lint/a11y/noAutofocus: the field appears because the user asked to type in it
      autoFocus
      aria-label={label}
      value={value}
      placeholder={placeholder}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={onKeyDown}
      onBlur={() => finish(value)}
      className="min-h-11 min-w-0 flex-1 rounded-xl border border-divider bg-surface-primary px-3 py-2 text-sm text-content-primary focus:border-primary-500 focus:outline-none"
    />
  );
}

export function IconButton({
  label,
  title,
  disabled,
  onClick,
  children,
}: {
  label: string;
  title?: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title ?? label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl text-content-secondary hover:bg-interactive-hover hover:text-content-primary disabled:opacity-30 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}
